import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './ClientGallery.css';
import { sizedImage, fallbackToOriginal } from '../utils/cloudinary';
import { GOOGLE_REVIEW_URL } from '../utils/brand';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');

function ClientGallery() {
  const { uniqueLink } = useParams();
  const [gallery, setGallery] = useState(null);
  // Paid hosting extension. Offered only inside the final week, where the
  // client already has a reason to care — an extend button on a gallery with a
  // month left is noise, and reads as a shakedown.
  const [extending, setExtending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Hotfix — Gallery Unarchive: the heading was hard-coded to "Gallery Not
  // Available" for every error case (404 / 410 / 403 / network), which
  // masked the actual error category. A real "Gallery Has Expired" looked
  // identical to "Gallery Not Found" in the heading — making the recent
  // archive→unarchive expiry bug invisible to admin troubleshooting.
  // Now the heading is driven by the same status code that drives the
  // message body.
  const [errorTitle, setErrorTitle] = useState('Gallery Not Available');
  // 402 — invoice outstanding. Distinct from an error: the client has done
  // nothing wrong and there is a clear way through.
  const [locked, setLocked] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [downloading, setDownloading] = useState(false);
  // The review ask lands best right after someone downloads their photos —
  // that's the moment they're happiest. Before this it sat statically at the
  // bottom of the page, competing with nothing and noticed by no one.
  const [hasDownloaded, setHasDownloaded] = useState(false);

  useEffect(() => {
    fetchGallery();
  }, [uniqueLink]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (selectedIndex === null) return;
    const photos = gallery?.photos || [];
    const handleKey = (e) => {
      if (e.key === 'Escape') setSelectedIndex(null);
      if (e.key === 'ArrowRight') setSelectedIndex(i => Math.min(i + 1, photos.length - 1));
      if (e.key === 'ArrowLeft') setSelectedIndex(i => Math.max(i - 1, 0));
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [selectedIndex, gallery]);

  // Paid hosting extension. MUST live at component scope — it is referenced by
  // the expiry banner's onClick in the render tree below.
  //
  // 2026-08-04: this was declared INSIDE the keyboard-navigation useEffect,
  // between the overflow assignment and the cleanup return. It was therefore
  // local to that effect callback and invisible to the JSX, so every gallery
  // inside its final 7 days threw `ReferenceError: handleExtend is not defined`
  // on render — the exact week a client is trying to download their photos, and
  // the button is the paid extension offer. `node --check` passes, babel parses,
  // and `react-scripts build` succeeds, because a ReferenceError on an
  // out-of-scope identifier is a RUNTIME failure. Lesson 32, re-learned.
  const handleExtend = async () => {
    setExtending(true);
    try {
      const res = await axios.post(`${API_BASE}/api/extensions/checkout/${uniqueLink}`);
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      alert('Could not start checkout. Please call (801) 210-0281 and we can extend it for you.');
    } catch (e) {
      alert(e.response?.data?.message || 'Could not start checkout. Please call (801) 210-0281 and we can extend it for you.');
    }
    setExtending(false);
  };

  const fetchGallery = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await axios.get(`${API_URL}/gallery/${uniqueLink}`);
      setGallery(response.data.data);
    } catch (err) {
      if (err.response?.status === 402) {
        // Not an error state — a pay screen. Keep it warm; the client may
        // simply not have seen the invoice yet.
        setLocked(err.response.data?.data || {});
      } else if (err.response?.status === 410) {
        setErrorTitle('Gallery Expired');
        setError('This gallery has expired and is no longer accessible.');
      } else if (err.response?.status === 404) {
        setErrorTitle('Gallery Not Found');
        setError('This gallery link is invalid or has been removed.');
      } else if (err.response?.status === 403) {
        setErrorTitle('Gallery Unavailable');
        setError('This gallery is not currently available. Please contact us if you believe this is an error.');
      } else {
        setErrorTitle('Gallery Not Available');
        setError('Unable to load gallery. Please try again or contact us for help.');
      }
    } finally {
      setLoading(false);
    }
  };

  const isVideo = (media) => {
    if (!media) return false;
    if (media.mediaType === 'video') return true;
    if (media.embedUrl) return true;
    const url = (media.url || '').toLowerCase();
    return url.endsWith('.mp4') || url.endsWith('.mov') || url.endsWith('.webm') || url.includes('/video/');
  };

  const getEmbedUrl = (url) => {
    if (!url) return '';
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return url;
  };

  const getMediaUrl = (media) => {
    if (!media) return '';
    if (media.url && media.url.startsWith('http')) return media.url;
    return `${API_BASE}${media.url}`;
  };

  const downloadMedia = async (media) => {
    setHasDownloaded(true);
    try {
      setDownloading(true);
      const url = media.downloadUrl || media.url;
      if (!url) return;

      const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
      const response = await fetch(fullUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const ext = isVideo(media) ? '.mp4' : '.jpg';
      link.download = media.filename || `download-${Date.now()}${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Download error:', err);
      const url = media.downloadUrl || media.url;
      if (url) window.open(url.startsWith('http') ? url : `${API_BASE}${url}`, '_blank');
    } finally {
      setDownloading(false);
    }
  };

  const downloadAll = async () => {
    setHasDownloaded(true);
    const photos = gallery?.photos || [];
    if (photos.length === 0) return;

    // Fire-and-forget download count increment. Tracking failures must NOT
    // block the actual download. Previously this never ran at all because
    // the only `trackDownload` helper in utils/api.js pointed at a URL that
    // didn't exist on the server — leaving downloadCount permanently at 0.
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    axios.post(`${API_URL}/gallery/${uniqueLink}/download`).catch(() => {
      // Silent — counter increment is best-effort
    });

    setDownloading(true);
    try {
      for (let i = 0; i < photos.length; i++) {
        await downloadMedia(photos[i]);
        if (i < photos.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    } catch (err) {
      console.error('Download all error:', err);
      alert('Some downloads may have failed');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="gallery-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading gallery...</p>
        </div>
      </div>
    );
  }

  if (locked) {
    const inv = locked.invoice || {};
    return (
      <div className="gallery-page">
        <div className="error-container">
          <div className="error-icon">🔒</div>
          <h2>Your photos are ready</h2>
          <p style={{ marginBottom: '1.25rem' }}>
            {locked.propertyAddress ? `${locked.propertyAddress} — ` : ''}
            {locked.photoCount ? `${locked.photoCount} photos, ` : ''}
            ready to download as soon as the invoice is settled.
          </p>
          {inv.invoiceNumber && (
            <div style={{
              background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)',
              borderRadius: '8px', padding: '1rem 1.25rem', margin: '0 auto 1.25rem', maxWidth: '340px'
            }}>
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                Invoice {inv.invoiceNumber}
              </p>
              <p style={{ margin: '0.25rem 0 0', fontSize: '1.6rem', fontWeight: 700, color: 'var(--text-strong)' }}>
                ${Number(inv.balanceDue || 0).toFixed(2)}
              </p>
            </div>
          )}
          {inv.payLink && (
            <a href={inv.payLink} style={{
              display: 'inline-block', background: '#2563eb', color: '#fff', padding: '0.85rem 2rem',
              borderRadius: '8px', textDecoration: 'none', fontWeight: 700, fontSize: '1rem'
            }}>Pay Invoice</a>
          )}
          <p style={{ marginTop: '1.25rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            The gallery unlocks automatically the moment payment goes through.
            Already paid, or something not right? Call (801) 210-0281.
          </p>
        </div>
      </div>
    );
  }

  if (error || !gallery) {
    return (
      <div className="gallery-page">
        <div className="error-container">
          <div className="error-icon">📸</div>
          <h2>{errorTitle}</h2>
          <p>{error || 'This gallery may have been removed or is no longer accessible.'}</p>
        </div>
      </div>
    );
  }

  const photos = gallery.photos || gallery.images || [];
  const videoCount = photos.filter(p => isVideo(p)).length;
  const imageCount = photos.length - videoCount;

  return (
    <div className="gallery-page">
      <div className="gallery-container">
        <div className="gallery-header">
          <h1>{gallery.title}</h1>
          {gallery.propertyAddress && <p className="address" style={{ color: 'white' }}>{gallery.propertyAddress}</p>}
          {gallery.description && <p className="description">{gallery.description}</p>}
          <div className="gallery-meta">
            {imageCount > 0 && <span>📸 {imageCount} Photo{imageCount !== 1 ? 's' : ''}</span>}
            {videoCount > 0 && <span>🎬 {videoCount} Video{videoCount !== 1 ? 's' : ''}</span>}
            {gallery.expiresAt && (
              <span>⏰ Available through {new Date(gallery.expiresAt).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
            )}
          </div>

          {/* Expiry notice.
              This used to be a bare "Expires 9/8/2026" in the meta row while the
              delivery email said "Tuesday, September 8, 2026" — same deadline,
              two formats, and the short numeric one is easy to skim past. Both
              now read identically.

              Subtle by default, escalating inside the final week: a client who
              has had the gallery for a month shouldn't be shouted at, but one
              with six days left should not be able to miss it. */}
          {gallery.expiresAt && (() => {
            const daysLeft = Math.ceil((new Date(gallery.expiresAt) - new Date()) / 86400000);
            if (daysLeft < 0) return null;
            const urgent = daysLeft <= 7;
            return (
              <div style={{
                background: urgent ? 'rgba(251,191,36,0.18)' : 'rgba(255,255,255,0.08)',
                border: `1px solid ${urgent ? 'rgba(251,191,36,0.55)' : 'rgba(255,255,255,0.18)'}`,
                borderRadius: '8px', padding: '10px 14px', margin: '14px 0 0',
                fontSize: '0.88rem', color: 'var(--on-fill)', lineHeight: 1.5
              }}>
                {urgent
                  ? <>
                      <strong>Download soon — {daysLeft} day{daysLeft === 1 ? '' : 's'} left.</strong> After this date the link stops working and files may be permanently deleted.
                      {' '}
                      <button
                        type="button"
                        onClick={handleExtend}
                        disabled={extending}
                        style={{
                          background: 'transparent', border: 'none', padding: 0,
                          color: 'var(--warning-bg)', fontWeight: 700, textDecoration: 'underline',
                          cursor: extending ? 'default' : 'pointer', fontSize: 'inherit', fontFamily: 'inherit'
                        }}
                      >
                        {extending ? 'Opening…' : 'Keep it online longer'}
                      </button>
                    </>
                  : <>Please download and save your files. This gallery stays online until the date above, after which the link stops working and files may be permanently deleted.</>}
              </div>
            );
          })()}
          {photos.length > 0 && (
            <button 
              className="btn-download-all"
              onClick={downloadAll}
              disabled={downloading}
            >
              {downloading ? 'Downloading...' : '⬇ Download All'}
            </button>
          )}
        </div>

        {/* External Links (Videos, 3D Tours, Floor Plans, etc.) */}
        {gallery.externalLinks && gallery.externalLinks.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {gallery.externalLinks.map((link, i) => {
                const icon = link.type === 'video' ? '🎬' : link.type === '3d_tour' ? '🏠' : link.type === 'floor_plan' ? '📐' : link.type === 'virtual_staging' ? '🛋️' : '🔗';
                const typeName = link.type === 'video' ? 'Video' : link.type === '3d_tour' ? '3D Tour' : link.type === 'floor_plan' ? 'Floor Plan' : link.type === 'virtual_staging' ? 'Virtual Staging' : 'Link';
                return (
                  <div key={link._id || i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)', borderRadius: '10px', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                      <span style={{ fontSize: '1.3rem' }}>{icon}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: '600', fontSize: '0.95rem', color: 'var(--text-heading)' }}>{link.label}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)' }}>{typeName}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                      <button onClick={() => {
                        navigator.clipboard.writeText(link.url).then(() => alert('Link copied!')).catch(() => prompt('Copy this link:', link.url));
                      }} style={{ padding: '8px 14px', background: 'var(--surface-muted)', border: '1px solid var(--border-strong)', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500' }}>
                        📋 Copy
                      </button>
                      <a href={link.url} target="_blank" rel="noopener noreferrer"
                        style={{ padding: '8px 14px', background: 'var(--fill-info)', color: 'var(--on-fill)', borderRadius: '6px', textDecoration: 'none', fontSize: '0.85rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center' }}>
                        Open ↗
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {photos.length > 0 ? (
          <div className="photos-grid">
            {photos.map((photo, index) => (
              <div 
                key={index} 
                className="photo-item"
                onClick={() => setSelectedIndex(index)}
              >
                {isVideo(photo) ? (
                  <div style={{position:'relative',width:'100%',height:'100%'}}>
                    {photo.url && photo.url.startsWith('http') ? (
                      <img 
                        src={getMediaUrl(photo)} 
                        alt={photo.caption || `Video ${index + 1}`}
                        style={{width:'100%',height:'100%',objectFit:'cover'}}
                        onError={(e) => { e.target.style.background='var(--text-strong)'; }}
                      />
                    ) : (
                      <div style={{width:'100%',height:'100%',background:'var(--text-strong)'}} />
                    )}
                    <div style={{
                      position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',
                      width:'50px',height:'50px',background:'rgba(0,0,0,0.6)',borderRadius:'50%',
                      display:'flex',alignItems:'center',justifyContent:'center',pointerEvents:'none'
                    }}>
                      <span style={{color:'white',fontSize:'20px',marginLeft:'3px'}}>▶</span>
                    </div>
                  </div>
                ) : (
                  <img
                    src={sizedImage(getMediaUrl(photo), 800)}
                    alt={photo.caption || `Photo ${index + 1}`}
                    loading="lazy"
                    decoding="async"
                    onError={fallbackToOriginal(getMediaUrl(photo))}
                  />
                )}
                {photo.caption && (
                  <div className="photo-caption">{photo.caption}</div>
                )}
                <div className="photo-overlay">
                  <button 
                    className="btn-view"
                    onClick={(e) => { e.stopPropagation(); setSelectedIndex(index); }}
                  >
                    View
                  </button>
                  <button 
                    className="btn-download"
                    onClick={(e) => { e.stopPropagation(); downloadMedia(photo); }}
                    disabled={downloading}
                  >
                    Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-gallery">
            <p>No photos available in this gallery yet.</p>
          </div>
        )}

        {photos.length > 0 && (
          <div style={{
            marginTop: '2rem', padding: '24px', textAlign: 'center',
            background: hasDownloaded ? 'var(--surface-sunken)' : 'var(--surface-raised)',
            border: hasDownloaded ? '1px solid #d4af37' : '1px solid var(--border-subtle)',
            boxShadow: hasDownloaded ? '0 4px 20px rgba(212,175,55,0.18)' : 'none',
            borderRadius: '12px',
            transition: 'background 0.4s ease, border-color 0.4s ease, box-shadow 0.4s ease'
          }}>
            <div style={{ fontSize: '1.4rem', marginBottom: '4px', letterSpacing: '2px' }}>⭐ ⭐ ⭐ ⭐ ⭐</div>
            <h3 style={{ margin: '0 0 6px', fontFamily: "'Playfair Display', serif", color: 'var(--text-strong)' }}>
              {hasDownloaded ? 'Got everything you need?' : 'Love your photos?'}
            </h3>
            <p style={{ margin: '0 0 16px', color: 'var(--text-muted)' }}>
              {hasDownloaded
                ? 'If the set turned out well, a quick Google review helps other agents find us. Takes about 30 seconds.'
                : 'A quick Google review helps other agents find us — it means a lot.'}
            </p>
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-block', padding: '12px 28px', background: 'var(--gold)', color: 'var(--on-fill)', borderRadius: '6px', textDecoration: 'none', fontWeight: 600 }}
            >
              Leave a Google Review
            </a>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {selectedIndex !== null && photos[selectedIndex] && (
        <div 
          className="lightbox" 
          onClick={() => setSelectedIndex(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="lightbox-close"
              onClick={() => setSelectedIndex(null)}
            >
              ✕
            </button>

            {/* Prev */}
            {photos.length > 1 && selectedIndex > 0 && (
              <button
                onClick={() => setSelectedIndex(i => i - 1)}
                style={{
                  position:'absolute',left:'15px',top:'50%',transform:'translateY(-50%)',
                  background:'rgba(0,0,0,0.5)',color:'white',border:'none',borderRadius:'50%',
                  width:'44px',height:'44px',fontSize:'20px',cursor:'pointer',zIndex:10
                }}
                aria-label="Previous"
              >‹</button>
            )}
            {/* Next */}
            {photos.length > 1 && selectedIndex < photos.length - 1 && (
              <button
                onClick={() => setSelectedIndex(i => i + 1)}
                style={{
                  position:'absolute',right:'15px',top:'50%',transform:'translateY(-50%)',
                  background:'rgba(0,0,0,0.5)',color:'white',border:'none',borderRadius:'50%',
                  width:'44px',height:'44px',fontSize:'20px',cursor:'pointer',zIndex:10
                }}
                aria-label="Next"
              >›</button>
            )}

            {/* Counter */}
            {photos.length > 1 && (
              <div style={{
                position:'absolute',top:'15px',left:'50%',transform:'translateX(-50%)',
                color:'white',fontSize:'14px',background:'rgba(0,0,0,0.5)',
                padding:'4px 12px',borderRadius:'12px',zIndex:10
              }}>
                {selectedIndex + 1} / {photos.length}
              </div>
            )}

            {/* Media display */}
            {(() => {
              const media = photos[selectedIndex];
              if (media.embedUrl) {
                return (
                  <div>
                    <iframe
                      key={selectedIndex}
                      src={getEmbedUrl(media.embedUrl)}
                      title={media.caption || 'Video'}
                      style={{
                        width:'min(90vw,960px)',height:'min(70vh,540px)',
                        border:'none',borderRadius:'4px'
                      }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                    {(media.downloadUrl || media.url) && (
                      <div style={{textAlign:'center',marginTop:'12px'}}>
                        <button
                          onClick={() => downloadMedia(media)}
                          disabled={downloading}
                          style={{
                            color:'white',background:'rgba(255,255,255,0.15)',padding:'8px 20px',
                            borderRadius:'6px',border:'1px solid rgba(255,255,255,0.3)',
                            cursor:'pointer',fontSize:'14px'
                          }}
                        >
                          {downloading ? 'Downloading...' : '⬇ Download Video'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              } else if (isVideo(media)) {
                return (
                  <video
                    key={selectedIndex}
                    src={getMediaUrl(media)}
                    controls
                    autoPlay
                    playsInline
                    style={{
                      maxWidth:'calc(100vw - 120px)',maxHeight:'calc(100vh - 140px)',
                      borderRadius:'4px'
                    }}
                  />
                );
              } else {
                return (
                  <img 
                    src={getMediaUrl(media)} 
                    alt={media.caption || 'Photo'} 
                    style={{
                      maxWidth:'calc(100vw - 120px)',maxHeight:'calc(100vh - 140px)',
                      objectFit:'contain',borderRadius:'4px'
                    }}
                  />
                );
              }
            })()}

            {photos[selectedIndex]?.caption && (
              <div className="lightbox-caption">{photos[selectedIndex].caption}</div>
            )}
            {!photos[selectedIndex]?.embedUrl && (
              <button 
                className="lightbox-download"
                onClick={() => downloadMedia(photos[selectedIndex])}
                disabled={downloading}
              >
                {downloading ? 'Downloading...' : '⬇ Download'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientGallery;
